import { Popover, PopoverAnchor } from '@chakra-ui/popover'
import { Portal } from '@chakra-ui/portal'
import { Flex } from '@chakra-ui/react'
import { useBoolean } from '@chakra-ui/react'
import { useRef } from 'react'
import iconSwap from '~/assets/svgs/icon-swap-arrows.svg?react'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { MenuPopoverWrapper } from '~/components/shared/Dropdown/Menu'
import QueueStatusMenu from '~/components/shared/Dropdown/QueueStatusMenu'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { QueueAgentStatus } from '~/hooks/useCallQueue'
import { useQueueAlerts } from '~/hooks/useQueueToasts'
import { useUserClickedExternalElements } from '~/hooks/useUserClickedExternalElements'
import {
  useQueueLogin,
  useQueueLogout,
  useUpdateUserAgentStatus,
} from '~/queries/queueQueries'
import { AgentCustomStatus } from '~/types/AgentCustomStatus'
import { Size } from '~/types/ThemeTypes'

import { ApiError, ExtensionPreview } from '@mangovoice/types'

const AgentStatusActionsCellContent = ({
  customStatuses,
  selectedValue,
  extension,
  onStatusUpdated,
}: {
  customStatuses: AgentCustomStatus[]
  selectedValue: QueueAgentStatus | number
  extension?: ExtensionPreview
  onStatusUpdated?: () => void
}) => {
  const params = {
    extensionId: extension?.id,
    options: {
      retry: 0,
    },
  }
  const { showQueueUpdateErrorModal, showStatusUpdatedToast } = useQueueAlerts()
  const queueLogin = useQueueLogin(params)
  const queueLogout = useQueueLogout(params)
  const updateUserAgentStatus = useUpdateUserAgentStatus({})

  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = useBoolean(false)

  const handleQueueError = (error: unknown) => {
    if ((error as ApiError)?.status === 404) {
      showQueueUpdateErrorModal()
    }
  }

  const handleStatusChange = async (value: QueueAgentStatus | number) => {
    switch (value) {
      case QueueAgentStatus.InQueue:
        try {
          await queueLogin()
          await updateUserAgentStatus(null, extension?.uuid, true)
          showStatusUpdatedToast()
          onStatusUpdated?.()
        } catch (error: unknown) {
          handleQueueError(error)
        }
        break
      case QueueAgentStatus.Offline:
        try {
          await queueLogout()
          await updateUserAgentStatus(null, extension?.uuid, false)
          showStatusUpdatedToast()
          onStatusUpdated?.()
        } catch (error: unknown) {
          handleQueueError(error)
        }
        break
      default:
        try {
          await queueLogout()
          if (typeof value === 'number') {
            await updateUserAgentStatus(value, extension?.uuid, false)
            showStatusUpdatedToast()
            onStatusUpdated?.()
          }
        } catch (error: unknown) {
          handleQueueError(error)
        }
        break
    }
  }

  const handleSelect = (value: QueueAgentStatus | number) => {
    void handleStatusChange(value)
  }

  useUserClickedExternalElements(
    [containerRef.current, buttonRef.current],
    true,
    () => {
      if (menuOpen) {
        setMenuOpen.off()
      }
    },
  )

  return (
    <Flex alignItems="center" gap="0.5rem" alignSelf="stretch">
      <Popover isOpen={menuOpen} placement="bottom-start">
        <PopoverAnchor>
          <QuickActionButton
            ref={buttonRef}
            tooltipDisabled={menuOpen}
            variant={QuickActionButtonVariants.Default_100}
            actionIcon={iconSwap}
            tooltipText="Change Status"
            size={Size.sm}
            onClick={setMenuOpen.toggle}
          />
        </PopoverAnchor>
        <Portal>
          {menuOpen && (
            <MenuPopoverWrapper ref={containerRef}>
              <QueueStatusMenu
                customStatuses={customStatuses ?? []}
                selectedValue={selectedValue}
                handleSelect={(value) => {
                  setMenuOpen.off()
                  handleSelect(value)
                }}
              />
            </MenuPopoverWrapper>
          )}
        </Portal>
      </Popover>
    </Flex>
  )
}

export default AgentStatusActionsCellContent
